import { Link } from 'react-router-dom';
import { Tent, ArrowLeft, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';

const PrivacyPage = () => {
  return (
    <div className="min-h-screen pb-20 md:pb-0">
      <div className="container mx-auto px-6 md:px-12 lg:px-24 py-8">
        {/* Back Link */}
        <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8">
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center">
              <Tent className="w-6 h-6 text-primary-foreground" />
            </div>
            <h1 className="font-heading font-bold text-3xl">Privacy Policy</h1>
          </div>

          <div className="bg-card rounded-2xl border border-border p-6 md:p-8 space-y-8">
            <p className="text-muted-foreground">
              Last updated: March 2026
            </p>

            <section className="space-y-4">
              <h2 className="font-heading font-semibold text-xl">1. What Data We Collect</h2>
              <p className="text-muted-foreground leading-relaxed">
                Campia collects the following personal information to operate our camp directory platform:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                <li><strong>Account Information:</strong> Name, email address, password (encrypted)</li>
                <li><strong>Profile Information:</strong> Bio, contact details, qualifications, years of experience, provinces, specialties</li>
                <li><strong>Verification Documents:</strong> ID documents, police clearance certificates, first aid certificates, qualifications</li>
                <li><strong>Review Content:</strong> Reviews submitted about camps and facilitators</li>
                <li><strong>Usage Data:</strong> Basic analytics about platform usage</li>
              </ul>
            </section>

            <section className="space-y-4">
              <h2 className="font-heading font-semibold text-xl">2. Why We Collect This Data</h2>
              <p className="text-muted-foreground leading-relaxed">
                We collect personal information for the following purposes:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                <li><strong>Directory Listing:</strong> To create and display public profiles for camps and facilitators</li>
                <li><strong>Verification:</strong> To verify the credentials and background of facilitators, building trust in our platform</li>
                <li><strong>Communication:</strong> To send important updates about your account, verification status, and platform changes</li>
                <li><strong>Platform Improvement:</strong> To understand how users interact with Campia and improve our services</li>
              </ul>
            </section>

            <section className="space-y-4">
              <h2 className="font-heading font-semibold text-xl">3. Who Can Access Your Data</h2>
              <p className="text-muted-foreground leading-relaxed">
                Your data is handled with care and only shared as necessary:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                <li><strong>Public Profile Information:</strong> Name, bio, contact details, qualifications, and verification status are visible to all visitors</li>
                <li><strong>Private Documents:</strong> Verification documents (ID, police clearance, certificates) are only accessible to Campia administrators and are never displayed publicly</li>
                <li><strong>Account Data:</strong> Only accessible to you and Campia administrators</li>
              </ul>
            </section>

            <section className="space-y-4">
              <h2 className="font-heading font-semibold text-xl">4. POPIA Compliance</h2>
              <p className="text-muted-foreground leading-relaxed">
                Campia is committed to complying with the Protection of Personal Information Act (POPIA) of South Africa. We:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                <li>Only collect information that is necessary for our stated purposes</li>
                <li>Obtain your consent before collecting personal information</li>
                <li>Keep your information secure using industry-standard encryption</li>
                <li>Allow you to access, correct, or delete your personal information</li>
                <li>Will notify you of any data breaches that may affect you</li>
              </ul>
            </section>

            <section className="space-y-4">
              <h2 className="font-heading font-semibold text-xl">5. Data Retention</h2>
              <p className="text-muted-foreground leading-relaxed">
                We retain your personal information for as long as your account is active or as needed to provide you services. 
                If you delete your account, we will delete your personal information within 30 days, except where we are required 
                to retain it for legal purposes.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="font-heading font-semibold text-xl">6. Your Rights</h2>
              <p className="text-muted-foreground leading-relaxed">
                Under POPIA, you have the right to:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                <li>Access your personal information</li>
                <li>Request correction of inaccurate information</li>
                <li>Request deletion of your personal information</li>
                <li>Object to processing of your personal information</li>
                <li>Lodge a complaint with the Information Regulator</li>
              </ul>
            </section>

            <section className="space-y-4">
              <h2 className="font-heading font-semibold text-xl">7. Contact Us</h2>
              <p className="text-muted-foreground leading-relaxed">
                For any data requests, questions, or concerns about your privacy, please contact us:
              </p>
              <div className="flex items-center gap-2 text-primary">
                <Mail className="w-4 h-4" />
                <a href="mailto:privacy@campia.co.za" className="hover:underline">privacy@campia.co.za</a>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;
